import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Opens the Stripe Customer Portal for the logged-in user (manage/cancel plan,
 * update card, view invoices). Mirrors useUpgrade():
 *   1. grab the current Supabase access token,
 *   2. POST it (Bearer) to /api/create-portal-session,
 *   3. redirect the browser to the returned portal url.
 *
 * Never throws into the UI — failures surface via `error`. On a successful
 * redirect `loading` stays true while the browser navigates away.
 */
export function usePortalSession() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPortal = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError('Please sign in again to manage your subscription.');
        setLoading(false);
        return;
      }

      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        let message = 'Could not open the billing portal. Please try again.';
        try {
          const body = await res.json();
          if (body && typeof body.error === 'string') message = body.error;
        } catch {
          /* non-JSON error body — keep the default message */
        }
        setError(message);
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data && typeof data.url === 'string' && data.url) {
        window.location.href = data.url;
        return;
      }

      setError('Could not open the billing portal. Please try again.');
      setLoading(false);
    } catch {
      setError('Could not open the billing portal. Please check your connection and try again.');
      setLoading(false);
    }
  }, []);

  return { openPortal, loading, error };
}
